// Sequential PCM16 audio player for realtime voice output.
//
// Audio arrives as a stream of base64 PCM16 chunks (24 kHz mono). Each chunk
// is decoded into an AudioBuffer and scheduled back-to-back on a single
// AudioContext timeline so playback is gapless. `onStateChange(speaking)`
// fires when the queue transitions between playing and idle.

import { SAMPLE_RATE, base64ToInt16, int16ToFloat32 } from './pcm.js';

export class VoicePlayer {
  constructor(onStateChange) {
    this.ctx = null;
    this.nextStartTime = 0;
    this.active = new Set();
    this.speaking = false;
    this.onStateChange = onStateChange || (() => {});
  }

  ensureContext() {
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this.ctx = new Ctx({ sampleRate: SAMPLE_RATE });
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    return this.ctx;
  }

  setSpeaking(value) {
    if (this.speaking === value) return;
    this.speaking = value;
    this.onStateChange(value);
  }

  enqueue(base64Pcm) {
    const ctx = this.ensureContext();
    const float = int16ToFloat32(base64ToInt16(base64Pcm));
    if (!float.length) return;

    const buffer = ctx.createBuffer(1, float.length, SAMPLE_RATE);
    buffer.copyToChannel(float, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, this.nextStartTime);
    source.start(startAt);
    this.nextStartTime = startAt + buffer.duration;

    this.active.add(source);
    this.setSpeaking(true);
    source.onended = () => {
      this.active.delete(source);
      if (this.active.size === 0) this.setSpeaking(false);
    };
  }

  /** Stop everything immediately (barge-in / interruption). */
  clear() {
    for (const source of this.active) {
      try { source.stop(); } catch (_e) { /* already stopped */ }
    }
    this.active.clear();
    this.nextStartTime = this.ctx ? this.ctx.currentTime : 0;
    this.setSpeaking(false);
  }

  async close() {
    this.clear();
    if (this.ctx) {
      try { await this.ctx.close(); } catch (_e) { /* ignore */ }
      this.ctx = null;
    }
  }
}
